import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/lunar-calendar.css';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import {
  getLunarDateDisplay, isVietnameseHoliday,
  solarToLunar, getLunarYearName, isAuspiciousDay,
  getHolidayName,
} from '../utils/lunarUtils';
import LunarCalendarCell from '../components/calendar/LunarCalendarCell';
import LunarEventModal from '../components/calendar/LunarEventModal';
import LunarDayAstrology from '../components/calendar/LunarDayAstrology';
import CustomMonthEvent from '../components/calendar/CustomEventWrapper';
import CustomCalendarToolbar from '../components/calendar/CustomCalendarToolbar';
import { CalendarEventsProvider } from '../contexts/CalendarEventsContext';
import {
  KPIStrip, KPI, Drawer, DrawerBody, DrawerSection, DrawerFooter,
  Button, Card,
} from '../shared/ui';
import * as Icons from '../shared/ui/icons';

const localizer = momentLocalizer(moment);

// 11 event types — colors only; labels come from i18n at render time
const EVENT_COLORS = {
  payment_due:     '#f59e0b',
  contract_expiry: '#ef4444',
  contract_start:  '#10b981',
  maintenance:     '#f97316',
  ticket:          '#f97316',
  inspection:      '#8b5cf6',
  lunar_holiday:   '#d97706',
  holiday:         '#dc2626',
  move_in:         '#0d9488',
  move_out:        '#78716c',
  custom:          '#3b82f6',
};

const SELECT_CLS = 'h-9 px-3 text-sm border border-stone-200 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-500';
const INPUT_CLS  = 'w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-500';
const LABEL_CLS  = 'block text-xs font-medium text-stone-600 mb-1';

const toCalendarEvent = (ev) => {
  const start = new Date(ev.event_date);
  let end = ev.end_date ? new Date(ev.end_date) : new Date(ev.event_date);
  if (ev.all_day && ev.end_date) {
    const s = new Date(ev.event_date), e = new Date(ev.end_date);
    if (e > s) { end = new Date(e); end.setDate(end.getDate() + 1); }
  }
  return { id: ev.id, title: ev.title, start, end, allDay: ev.all_day, resource: ev };
};

const filterEvents = (rawEvents) =>
  (rawEvents || [])
    .filter(ev => {
      if (ev.event_type !== 'payment_due') return true;
      if (!ev.title || ev.title.includes('N/A')) return false;
      const meta = typeof ev.metadata === 'string'
        ? (() => { try { return JSON.parse(ev.metadata); } catch { return {}; } })()
        : (ev.metadata || {});
      return !!(meta.room_number || (ev.title || '').match(/:\s*[A-Z]?\d+/));
    })
    .map(toCalendarEvent);

/** Roll multiple payment_due events on the same date/kind into one summary chip.
 *  Non-payment events pass through unchanged. */
const aggregatePaymentEvents = (evs, t) => {
  const groups = new Map();
  const passthrough = [];
  for (const ev of evs) {
    if (ev.resource?.event_type !== 'payment_due') {
      passthrough.push(ev);
      continue;
    }
    const meta = typeof ev.resource.metadata === 'string'
      ? (() => { try { return JSON.parse(ev.resource.metadata); } catch { return {}; } })()
      : (ev.resource.metadata || {});
    const kind = meta.kind || 'due';
    const dateKey = new Date(ev.start).toDateString();
    const key = `${dateKey}|${kind}`;
    if (!groups.has(key)) groups.set(key, { date: new Date(ev.start), kind, events: [] });
    groups.get(key).events.push(ev);
  }

  const aggregated = [];
  for (const [key, { date, kind, events }] of groups) {
    const count = events.length;
    const statuses = events.map(e => e.resource?.status);
    const paidCount = statuses.filter(s => s === 'completed').length;
    const overdueCount = statuses.filter(s => s === 'overdue').length;

    let title, status, priority;
    if (kind === 'overdue') {
      title = t('aggregated.overdue', { count });
      status = 'overdue';
      priority = 'urgent';
    } else if (paidCount === count) {
      title = t('aggregated.allPaid', { count });
      status = 'completed';
      priority = 'low';
    } else if (overdueCount > 0) {
      title = t('aggregated.dueWithOverdue', { count, overdue: overdueCount });
      status = 'overdue';
      priority = 'high';
    } else if (paidCount > 0) {
      title = t('aggregated.dueWithPaid', { count, paid: paidCount });
      status = 'scheduled';
      priority = 'medium';
    } else {
      title = t('aggregated.due', { count });
      status = 'scheduled';
      priority = 'medium';
    }

    aggregated.push({
      id: `agg-${key}`,
      title,
      start: date,
      end: date,
      allDay: true,
      resource: {
        event_type: 'payment_due',
        status,
        priority,
        aggregated: true,
        description: t('aggregated.description', { count }),
        metadata: { kind, count, items: events.map(e => e.resource) },
      },
    });
  }

  return [...passthrough, ...aggregated];
};

export default function CalendarPage() {
  const qc = useQueryClient();
  const { t, i18n } = useTranslation(['calendar', 'common']);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const [navDate, setNavDate]       = useState(new Date());
  const [building, setBuilding]     = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayEvents, setDayEvents]   = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showLunar, setShowLunar]   = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', event_type: 'custom', event_date: '',
    end_date: '', all_day: true, priority: 'medium', is_public: false,
  });
  const [recur, setRecur] = useState({ enabled: false, freq: 'WEEKLY', interval: 1 });
  const [showDayDrawer, setShowDayDrawer] = useState(false);
  const [viewMode, setViewMode] = useState('summary');

  const WEEKDAYS = t('weekday.long', { returnObjects: true });
  const MONTHS_SHORT = t('month.short', { returnObjects: true });
  const WEEKDAYS_SHORT = t('weekday.short', { returnObjects: true });

  const range = useMemo(() => ({
    start: moment(navDate).subtract(3, 'months').startOf('month').toISOString(),
    end:   moment(navDate).add(3, 'months').endOf('month').toISOString(),
  }), [navDate]);

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings'],
    queryFn: () => api.get('/api/properties/buildings').then(r => r.data),
    staleTime: 300_000,
  });

  const { data: rawEvents = [], isLoading } = useQuery({
    queryKey: ['calendar-events', range.start, range.end, building],
    queryFn: () => api.get('/api/calendar/admin/events', {
      params: { start_date: range.start, end_date: range.end, ...(building ? { building_id: building } : {}) },
    }).then(r => r.data),
    staleTime: 60_000,
  });

  const rawFiltered = useMemo(() => filterEvents(rawEvents), [rawEvents]);
  const events = useMemo(
    () => viewMode === 'summary' ? aggregatePaymentEvents(rawFiltered, t) : rawFiltered,
    [rawFiltered, viewMode, t, lang]
  );

  // KPI counts for current month
  const now = new Date();
  const thisMonth = useMemo(() => events.filter(ev => {
    const d = new Date(ev.start);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }), [events]);

  const todayPayments = useMemo(() =>
    events.filter(ev => ev.resource?.event_type === 'payment_due' && new Date(ev.start).toDateString() === now.toDateString()).length,
    [events]);

  const expiryCount = useMemo(() =>
    events.filter(ev => {
      if (ev.resource?.event_type !== 'contract_expiry') return false;
      const days = Math.ceil((new Date(ev.start) - now) / 86400000);
      return days >= 0 && days <= 30;
    }).length,
    [events]);

  const pendingCount = useMemo(() =>
    events.filter(ev => ['ticket', 'maintenance'].includes(ev.resource?.event_type)).length,
    [events]);

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/api/calendar/events', payload).then(r => r.data),
    onSuccess: () => {
      toast.success(t('toast.created'));
      qc.invalidateQueries({ queryKey: ['calendar-events'] });
      setShowCreate(false);
      setForm({ title: '', description: '', event_type: 'custom', event_date: '', end_date: '', all_day: true, priority: 'medium', is_public: false });
      setRecur({ enabled: false, freq: 'WEEKLY', interval: 1 });
    },
    onError: () => toast.error(t('toast.createFailed')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/calendar/events/${id}`).then(r => r.data),
    onSuccess: () => {
      toast.success(t('toast.deleted'));
      qc.invalidateQueries({ queryKey: ['calendar-events'] });
      setShowDayDrawer(false);
    },
    onError: () => toast.error(t('toast.deleteFailed')),
  });

  const selectDay = (date, evList) => {
    setSelectedDay(date);
    setDayEvents(evList ?? rawFiltered.filter(ev =>
      new Date(ev.start).toDateString() === new Date(date).toDateString()
    ));
    setShowDayDrawer(true);
  };

  const handleSelectSlot = (info) => selectDay(info.start);
  const handleSelectEvent = (ev) => selectDay(new Date(ev.start));

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate({
      ...form,
      building_id: building || null,
      recurrence_rule: recur.enabled ? `FREQ=${recur.freq};INTERVAL=${recur.interval}` : null,
    });
  };

  const eventStyleGetter = (ev) => {
    const type   = ev.resource?.event_type;
    const status = ev.resource?.status;
    let bg = EVENT_COLORS[type] || '#6b7280';
    let borderLeft = 'none';
    if (type === 'payment_due') {
      if (['completed', 'paid', 'confirmed'].includes(status)) { bg = '#10b981'; borderLeft = '3px solid #059669'; }
      else if (status === 'overdue')  { bg = '#ef4444'; borderLeft = '3px solid #dc2626'; }
      else if (status === 'partial')  { bg = '#3b82f6'; borderLeft = '3px solid #2563eb'; }
      else                            { borderLeft = '3px solid #d97706'; }
    }
    if (ev.resource?.priority === 'urgent') { bg = '#dc2626'; borderLeft = '3px solid #991b1b'; }
    return { style: { backgroundColor: bg, borderRadius: '4px', opacity: 0.9, color: '#fff', border: 'none', borderLeft, display: 'block', fontWeight: 600 } };
  };

  // Day rail computed values
  const lunar      = selectedDay ? solarToLunar(selectedDay) : null;
  const lunarDisp  = selectedDay ? getLunarDateDisplay(selectedDay, 'long') : null;
  const holiday    = selectedDay ? isVietnameseHoliday(selectedDay) : null;
  const auspicious = selectedDay ? isAuspiciousDay(selectedDay) : false;

  const groupedDayEvents = useMemo(() => {
    const g = {};
    dayEvents.forEach(ev => {
      const ty = ev.resource?.event_type || 'custom';
      (g[ty] = g[ty] || []).push(ev);
    });
    return g;
  }, [dayEvents]);

  const renderDayDetail = () => {
    if (!selectedDay) return null;
    return (
      <>
        <div className="bg-amber-50 border-b border-amber-100 px-4 py-2.5">
          <p className="text-xs font-medium text-amber-700">{lunarDisp}</p>
          {lunar && (
            <p className="text-xs text-amber-600 mt-0.5">
              {t('dayDetail.yearLine', { year: lunar.year, zodiac: getLunarYearName(lunar.year) })}
            </p>
          )}
          {holiday && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: holiday.color }}>
              {getHolidayName(holiday, lang)}
            </div>
          )}
          {auspicious && !holiday && (
            <p className="text-xs text-red-600 mt-1 font-medium">{t('dayDetail.auspicious')}</p>
          )}
        </div>

        <div className="overflow-y-auto">
          {dayEvents.length === 0 ? (
            <p className="py-6 text-center text-sm text-stone-400 px-4">{t('dayDetail.noEvents')}</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {Object.entries(groupedDayEvents).map(([type, evList]) => {
                const color = EVENT_COLORS[type] || '#6b7280';
                const typeLabel = t(`event.type.${type}`, { defaultValue: type });
                return (
                  <div key={type} className="px-4 py-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">{typeLabel}</span>
                      <span className="ml-auto text-xs text-stone-400">{evList.length}</span>
                    </div>
                    <div className="space-y-1.5">
                      {evList.map(ev => (
                        <div key={ev.id} className="flex items-start gap-2 text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-stone-800 truncate">{ev.title}</p>
                            {ev.resource?.description && (
                              <p className="text-xs text-stone-500 truncate">{ev.resource.description}</p>
                            )}
                          </div>
                          {ev.resource?.event_type === 'custom' && (
                            <button
                              onClick={() => {
                                if (window.confirm(t('dayDetail.confirmDelete')))
                                  deleteMutation.mutate(ev.resource.parent_event_id || ev.id);
                              }}
                              className="p-0.5 text-stone-300 hover:text-rose-500 shrink-0"
                            >
                              <Icons.Trash size={13} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-t border-stone-100">
            <LunarDayAstrology date={selectedDay} />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-stone-100">
          <Button
            kind="primary" size="sm"
            className="w-full justify-center"
            iconLeft={<Icons.Plus size={14} />}
            onClick={() => {
              setForm(f => ({ ...f, event_date: moment(selectedDay).format('YYYY-MM-DDTHH:mm') }));
              setShowDayDrawer(false);
              setShowCreate(true);
            }}
          >
            {t('action.createForDay')}
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-stone-900">{t('title')}</h1>
          <p className="text-sm text-stone-500 mt-0.5">{t('subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button kind="ghost" size="sm" onClick={() => setShowLunar(true)} iconLeft={<Icons.Moon size={14} />}>
            {t('action.lunarHolidays')}
          </Button>
          <Button
            kind="primary" size="sm"
            onClick={() => { setForm(f => ({ ...f, event_date: moment().format('YYYY-MM-DDTHH:mm') })); setShowCreate(true); }}
            iconLeft={<Icons.Plus size={14} />}
          >
            {t('action.createEvent')}
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <KPIStrip columns={4}>
        <KPI label={t('kpi.thisMonth')}    value={thisMonth.length}  icon={<Icons.Calendar size={14} />} accent="primary" />
        <KPI label={t('kpi.todayPayments')} value={todayPayments}     icon={<Icons.DollarSign size={14} />} accent={todayPayments > 0 ? 'success' : 'neutral'} />
        <KPI label={t('kpi.expiring')}     value={expiryCount}       icon={<Icons.Alert size={14} />}    accent={expiryCount > 0 ? 'warning' : 'neutral'} />
        <KPI label={t('kpi.pending')}      value={pendingCount}      icon={<Icons.Clock size={14} />}    accent={pendingCount > 0 ? 'warning' : 'neutral'} />
      </KPIStrip>

      {/* Filter + legend */}
      <Card padding={12}>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={building || ''}
            onChange={e => setBuilding(e.target.value ? +e.target.value : null)}
            className={`${SELECT_CLS} w-56`}
          >
            <option value="">{t('filter.allBuildings')}</option>
            {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>

          {/* View mode toggle */}
          <div className="inline-flex rounded-md border border-stone-200 bg-stone-50 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setViewMode('summary')}
              className={`px-2.5 py-1 rounded ${viewMode === 'summary' ? 'bg-white shadow-sm text-stone-900 font-semibold' : 'text-stone-500 hover:text-stone-700'}`}
              title={t('viewMode.summaryHint')}
            >
              {t('viewMode.summary')}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('full')}
              className={`px-2.5 py-1 rounded ${viewMode === 'full' ? 'bg-white shadow-sm text-stone-900 font-semibold' : 'text-stone-500 hover:text-stone-700'}`}
              title={t('viewMode.fullHint')}
            >
              {t('viewMode.full')}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 ml-auto">
            {Object.entries(EVENT_COLORS).map(([type, color]) => (
              <span key={type} className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-stone-50 border border-stone-100 text-stone-600">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                {t(`event.type.${type}`)}
              </span>
            ))}
          </div>
        </div>
      </Card>

      {/* Calendar */}
      <Card padding={0} className="overflow-hidden" style={{ height: 'calc(100vh - 290px)', minHeight: 620 }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full gap-2 text-stone-400">
            <Icons.Spinner size={20} className="animate-spin text-teal-600" />
            <span className="text-sm">{t('loading')}</span>
          </div>
        ) : (
          <CalendarEventsProvider events={events}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              date={navDate}
              onNavigate={setNavDate}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              onDrillDown={(date) => selectDay(date)}
              onShowMore={(_evts, date) => selectDay(date)}
              selectable
              popup
              eventPropGetter={eventStyleGetter}
              views={['month', 'week', 'day', 'agenda']}
              defaultView="month"
              className="premium-calendar"
              messages={{
                showMore: (total) => t('showMore', { count: total }),
              }}
              components={{
                toolbar: CustomCalendarToolbar,
                month: { dateHeader: LunarCalendarCell, event: CustomMonthEvent },
              }}
              formats={{
                weekdayFormat: (date) => (Array.isArray(WEEKDAYS_SHORT) ? WEEKDAYS_SHORT : ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'])[date.getDay()],
              }}
              style={{ height: '100%', width: '100%' }}
            />
          </CalendarEventsProvider>
        )}
      </Card>

      {/* Day detail drawer */}
      <Drawer
        open={showDayDrawer && !!selectedDay}
        onClose={() => setShowDayDrawer(false)}
        title={
          selectedDay && Array.isArray(MONTHS_SHORT)
            ? `${selectedDay.getDate()} ${MONTHS_SHORT[selectedDay.getMonth()]} ${selectedDay.getFullYear()}`
            : ''
        }
        subtitle={
          selectedDay && Array.isArray(WEEKDAYS)
            ? `${WEEKDAYS[selectedDay.getDay()]}${holiday ? ' · ' + getHolidayName(holiday, lang) : ''}`
            : ''
        }
        width={380}
      >
        <DrawerBody className="p-0">
          {renderDayDetail()}
        </DrawerBody>
      </Drawer>

      {/* Create event drawer */}
      <Drawer open={showCreate} onClose={() => setShowCreate(false)} title={t('create.title')} width={420}>
        <form onSubmit={handleSubmit}>
          <DrawerBody>
            <DrawerSection title={t('create.section.info')}>
              <div className="space-y-3">
                <div>
                  <label className={LABEL_CLS}>{t('create.field.title')} *</label>
                  <input
                    type="text" required
                    className={INPUT_CLS}
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder={t('create.field.titlePlaceholder')}
                  />
                </div>
                <div>
                  <label className={LABEL_CLS}>{t('create.field.description')}</label>
                  <textarea
                    rows={3}
                    className={`${INPUT_CLS} resize-y min-h-[72px]`}
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder={t('create.field.descriptionPlaceholder')}
                  />
                </div>
              </div>
            </DrawerSection>

            <DrawerSection title={t('create.section.typePriority')}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL_CLS}>{t('create.field.eventType')}</label>
                  <select
                    className={`${SELECT_CLS} w-full`}
                    value={form.event_type}
                    onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}
                  >
                    <option value="custom">{t('create.type.custom')}</option>
                    <option value="maintenance">{t('create.type.maintenance')}</option>
                    <option value="inspection">{t('create.type.inspection')}</option>
                    <option value="move_in">{t('create.type.move_in')}</option>
                    <option value="move_out">{t('create.type.move_out')}</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLS}>{t('create.field.priority')}</label>
                  <select
                    className={`${SELECT_CLS} w-full`}
                    value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                  >
                    <option value="low">{t('create.priority.low')}</option>
                    <option value="medium">{t('create.priority.medium')}</option>
                    <option value="high">{t('create.priority.high')}</option>
                    <option value="urgent">{t('create.priority.urgent')}</option>
                  </select>
                </div>
              </div>
            </DrawerSection>

            <DrawerSection title={t('create.section.time')}>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL_CLS}>{t('create.field.startDate')} *</label>
                    <input
                      type="datetime-local" required
                      className={INPUT_CLS}
                      value={form.event_date}
                      onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>{t('create.field.endDate')}</label>
                    <input
                      type="datetime-local"
                      className={INPUT_CLS}
                      value={form.end_date}
                      onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.all_day}
                    onChange={e => setForm(f => ({ ...f, all_day: e.target.checked }))}
                    className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                  />
                  {t('create.field.allDay')}
                </label>
              </div>
            </DrawerSection>

            <DrawerSection title={t('create.section.recurrence')}>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recur.enabled}
                    onChange={e => setRecur(r => ({ ...r, enabled: e.target.checked }))}
                    className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                  />
                  {t('create.field.enableRecurrence')}
                </label>
                {recur.enabled && (
                  <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-teal-100 mt-2">
                    <div>
                      <label className={LABEL_CLS}>{t('create.field.frequency')}</label>
                      <select
                        className={`${SELECT_CLS} w-full`}
                        value={recur.freq}
                        onChange={e => setRecur(r => ({ ...r, freq: e.target.value }))}
                      >
                        <option value="DAILY">{t('create.frequency.DAILY')}</option>
                        <option value="WEEKLY">{t('create.frequency.WEEKLY')}</option>
                        <option value="MONTHLY">{t('create.frequency.MONTHLY')}</option>
                        <option value="YEARLY">{t('create.frequency.YEARLY')}</option>
                      </select>
                    </div>
                    <div>
                      <label className={LABEL_CLS}>{t('create.field.interval')}</label>
                      <input
                        type="number" min="1"
                        className={INPUT_CLS}
                        value={recur.interval}
                        onChange={e => setRecur(r => ({ ...r, interval: +e.target.value }))}
                      />
                    </div>
                  </div>
                )}
              </div>
            </DrawerSection>

            <DrawerSection title={t('create.section.visibility')}>
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))}
                  className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
                {t('create.field.showToRenters')}
              </label>
            </DrawerSection>
          </DrawerBody>

          <DrawerFooter>
            <Button kind="primary" type="submit" loading={createMutation.isPending} iconLeft={<Icons.Plus size={14} />}>
              {t('create.submit')}
            </Button>
            <Button kind="ghost" type="button" onClick={() => setShowCreate(false)}>{t('create.cancel')}</Button>
          </DrawerFooter>
        </form>
      </Drawer>

      {/* Lunar event modal */}
      <LunarEventModal
        isOpen={showLunar}
        onClose={() => setShowLunar(false)}
        onEventCreated={() => qc.invalidateQueries({ queryKey: ['calendar-events'] })}
      />
    </div>
  );
}
